import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink, SupTitle } from "components";
import { useAnchor } from "hooks";
import { Features35Content } from "types/graphql/generated";

interface Props {
  content: Features35Content;
}

const Features35: VFC<Props> = withErrorBoundary(({ content: { features35SupTitle, features35List } }) => {
  useAnchor("features35");

  return (
    <StyledFeatures35 className="features35 Content">
      <div>
        <SupTitle>{features35SupTitle}</SupTitle>
      </div>
      <ul className="Features35__list">
        {features35List.map(({ id, title, description, features35Link, features35LinkLabel }) => (
          <li key={id} className="Features35__listItem">
            <div>
              <h2 className="Font_32_40_600 md:Font_24_32_600">{title}</h2>
              <Markdown className="Features35__description Color_secondary Font_18_26_450">{description}</Markdown>
            </div>
            {features35Link && (
              <ArrowLink href={features35Link} className="Features35__link Font_18_26_600">
                {features35LinkLabel}
              </ArrowLink>
            )}
          </li>
        ))}
      </ul>
    </StyledFeatures35>
  );
}, {});

const StyledFeatures35 = styled.section`
  padding-top: 64px;
  margin-top: -64px;

  .Features35__list {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 32px;
    margin-top: 32px;
    list-style: none;
  }

  .Features35__listItem {
    padding: 32px 24px;
    background: ${({ theme }) => theme.colors.fog["05"]};
    border: 1px solid ${({ theme }) => theme.colors.fog["30"]};
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .Features35__description {
    margin-top: 8px;
  }

  .Features35__link {
    margin-top: 24px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Features35__list {
      grid-template-columns: 1fr 1fr;
      grid-auto-rows: 1fr;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features35__list {
      grid-template-columns: 1fr;
    }
  }
`;

gql`
  fragment Features35Content on ComponentFeaturesFeatures35 {
    features35SupTitle
    features35List {
      id
      title
      description
      features35Link
      features35LinkLabel
    }
  }
`;

export { Features35 };
export type { Props as Features35Props };
